/**
 * ExportDialog — centred modal to configure and trigger a canvas export.
 * Calls onExport({ type, quality, transparent, filename }); the caller owns the
 * actual canvas render + download.
 */
import React, { useState, useEffect } from 'react';
import { X, Download } from 'lucide-react';
import { T } from '../../../components/em/tokens';

const fieldLabel = { display: 'block', font: `500 12px/1.3 ${T.font}`, color: T.fg3, marginBottom: 6 };

const inputStyle = {
  width: '100%', padding: '9px 12px', boxSizing: 'border-box',
  border: `1px solid ${T.border}`, borderRadius: T.rMd,
  background: T.bgCard, color: T.fg1, font: `400 13px/1.3 ${T.font}`, outline: 'none',
};

function Segmented({ options, value, onChange }) {
  return (
    <div style={{ display: 'inline-flex', padding: 3, background: T.bgTab, borderRadius: T.rLg, gap: 2, width: '100%' }}>
      {options.map((opt) => {
        const on = value === opt.key;
        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => onChange(opt.key)}
            style={{
              flex: 1, padding: '7px 10px', borderRadius: T.rMd, border: 0, cursor: 'pointer',
              background: on ? T.bgCard : 'transparent',
              color: on ? T.orange : T.fg5,
              font: `${on ? 600 : 500} 13px/1 ${T.font}`,
              boxShadow: on ? T.shadowSm : 'none',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export default function ExportDialog({ open, onClose, onExport, defaultName }) {
  const [format, setFormat] = useState('png');
  const [quality, setQuality] = useState(0.92);
  const [transparent, setTransparent] = useState(false);
  const [filename, setFilename] = useState(defaultName || 'profile');

  // Re-seed the filename whenever the dialog is (re)opened.
  useEffect(() => {
    if (open) setFilename(defaultName || 'profile');
  }, [open, defaultName]);

  if (!open) return null;

  const submit = () => {
    onExport({
      type: format,
      quality,
      transparent: format === 'png' ? transparent : false,
      filename: (filename || 'profile').trim() || 'profile',
    });
  };

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Export image"
        style={{ width: 380, maxWidth: '100%', background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: T.rXl, boxShadow: T.shadowMd, overflow: 'hidden' }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 18px', borderBottom: `1px solid ${T.divider}` }}>
          <span style={{ font: `700 16px/1.2 ${T.font}`, color: T.fg1 }}>Export image</span>
          <button type="button" onClick={onClose} title="Close" aria-label="Close" style={{ width: 28, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: 0, background: 'transparent', borderRadius: T.rSm, cursor: 'pointer', color: T.fg5 }}>
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <span style={fieldLabel}>Format</span>
            <Segmented
              options={[{ key: 'png', label: 'PNG' }, { key: 'jpg', label: 'JPG' }]}
              value={format}
              onChange={setFormat}
            />
          </div>

          {format === 'jpg' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={fieldLabel}>Quality</span>
                <span style={{ font: `500 12px/1 ${T.font}`, color: T.fg5 }}>{Math.round(quality * 100)}%</span>
              </div>
              <input
                type="range"
                min={0.5}
                max={1}
                step={0.01}
                value={quality}
                onChange={(e) => setQuality(Number(e.target.value))}
                style={{ width: '100%', accentColor: 'var(--em-orange)' }}
              />
            </div>
          )}

          {format === 'png' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={transparent}
                onChange={(e) => setTransparent(e.target.checked)}
                style={{ width: 16, height: 16, accentColor: 'var(--em-orange)', cursor: 'pointer' }}
              />
              <span style={{ font: `500 13px/1.3 ${T.font}`, color: T.fg2 }}>Transparent background</span>
            </label>
          )}

          <div>
            <span style={fieldLabel}>File name</span>
            <input
              type="text"
              value={filename}
              onChange={(e) => setFilename(e.target.value)}
              placeholder="profile"
              style={inputStyle}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '14px 18px', borderTop: `1px solid ${T.divider}` }}>
          <button
            type="button"
            onClick={onClose}
            style={{ padding: '9px 16px', borderRadius: T.rMd, border: `1px solid ${T.border}`, background: 'transparent', color: T.fg2, cursor: 'pointer', font: `500 13px/1 ${T.font}` }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: T.rMd, border: 0, background: T.gradient, color: '#fff', cursor: 'pointer', font: `600 13px/1 ${T.font}`, boxShadow: T.shadowBrand }}
          >
            <Download size={15} />Download
          </button>
        </div>
      </div>
    </div>
  );
}
